import { observer } from "mobx-react-lite";
import { useCallback, useState } from "react";
import { ChartBarIcon, ViewListIcon } from "@heroicons/react/outline";
import {
  ChartBarIcon as ChartBarSolidIcon,
  ViewListIcon as ViewListIconSolid,
} from "@heroicons/react/solid";

import ConfigurationView from "./ConfigurationView";
import { useWorkflow } from "./hooks";

import { WorkflowConfiguration } from "@/types/evaluate";
import { BlockProps, isEvalLikeEnv, useBlockEnv } from "@/components/PowerBlock/Block";
import { withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import TabNavigation from "@/components/ui/TabNavigation";
import BlockStatistics from "@/components/PowerBlock/Block/BlockStatistics";

const validationSchema = createBlockSchema<WorkflowConfiguration>()
  .requireName()
  .addRule(required("configuration.name", "Workflow selection is required"))
  .build();

export const WorkflowBlockCore = observer(
  ({
    showVerboseMode = true,
    sources = createEmptySources(),
    id,
    readonly,
  }: Omit<BlockProps<WorkflowConfiguration>, "DefaultFields"> & {
    showVerboseMode?: boolean;
  }) => {
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const env = useBlockEnv();
    const isEval = isEvalLikeEnv(env);
    const {
      blockStore,
      workflowId,
      folderId,
      inputVariables,
      handleWorkflowSelection,
      handleVerboseChange,
      handleReturnAllOutputsChange,
      hasError,
      isLoading,
      inputVariablesIsLoading,
    } = useWorkflow();

    const handleTabChange = (tabId: number) => {
      setCurrentTabIndex(tabId);
    };

    const handleAutofill = useCallback(
      (mappings: Record<string, string>) => {
        if (blockStore.configuration) {
          blockStore.setConfiguration({
            ...blockStore.configuration,
            input_variables: {
              ...blockStore.configuration.input_variables,
              ...mappings,
            },
          });
        }
      },
      [blockStore],
    );

    const configurationContent = (
      <ConfigurationView
        workflowName={blockStore.configuration?.name}
        workflowId={workflowId}
        workflowVersionNumber={blockStore.configuration?.workflow_version_number}
        workflowLabelName={blockStore.configuration?.workflow_label_name}
        folderId={folderId}
        isLoading={isLoading}
        inputVariables={inputVariables}
        sources={sources}
        showVerboseMode={showVerboseMode}
        verboseEnabled={blockStore.configuration?.verbose || false}
        returnAllOutputsEnabled={blockStore.configuration?.return_all_outputs || false}
        inputVariablesIsLoading={inputVariablesIsLoading}
        onWorkflowSelection={handleWorkflowSelection}
        onVerboseChange={handleVerboseChange}
        onReturnAllOutputsChange={handleReturnAllOutputsChange}
        onAutofill={handleAutofill}
        hasError={hasError}
      />
    );

    return (
      <>
        {readonly && isEval ? (
          <TabNavigation
            selectedIndex={currentTabIndex}
            handleTabChange={handleTabChange}
            tabs={[
              {
                id: 0,
                name: "Overview",
                outlineIcon: <ViewListIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />,
                solidIcon: <ViewListIconSolid className="mr-1.5 h-4 w-4" aria-hidden="true" />,
                content: <div className="pt-2">{configurationContent}</div>,
              },
              {
                id: 1,
                name: "Statistics",
                outlineIcon: <ChartBarIcon className="h-4 w-4" />,
                solidIcon: <ChartBarSolidIcon className="h-4 w-4" />,
                content: <BlockStatistics id={id as number} />,
              },
            ]}
          />
        ) : (
          configurationContent
        )}
      </>
    );
  },
);

export default withBlockStore<WorkflowConfiguration>(WorkflowBlockCore, {
  validationSchema,
});
